import { useEffect, useState } from "react";
import { Printer, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { deductionHtml } from "@/lib/deductionForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DateInput } from "@/components/ui/date-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const KIND_LABELS = { absence: "قرار حسم غياب", lateness: "قرار حسم تأخر" };

const waitForPrintImages = (doc) => {
  const imgs = [...doc.images];
  if (!imgs.length) return Promise.resolve();
  return Promise.all(imgs.map((img) => new Promise((res) => {
    if (img.complete) return res();
    img.onload = img.onerror = res;
    if (img.decode) img.decode().then(res).catch(() => {});
    setTimeout(res, 4000);
  })));
};

// دمج عدة قرارات في مستند طباعة واحد — كل قرار في صفحة مستقلة
const combinedDeductionsHtml = (list, branding) => {
  const first = deductionHtml(list[0], branding);
  const head = first.split("<body>")[0];
  const pages = list.map((d) => {
    const body = deductionHtml(d, branding).split("<body>")[1].split("</body>")[0];
    return `<div style="page-break-after: always;">${body}</div>`;
  });
  return `${head}<body>${pages.join("")}</body></html>`;
};

export default function DeductionsTab() {
  const [deductions, setDeductions] = useState([]);
  const [branding, setBranding] = useState({});
  const [dedEdit, setDedEdit] = useState(null);
  const [dedDays, setDedDays] = useState(1);
  const [dedDetails, setDedDetails] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [empFilter, setEmpFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    api.get("/deductions").then((r) => setDeductions(r.data)).catch(() => {});
    api.get("/branding").then((r) => setBranding(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const empOptions = [...new Set(deductions.map((d) => d.employee_name).filter(Boolean))].sort();
  const kindOptions = [...new Set(deductions.map((d) => d.kind).filter(Boolean))];

  const visibleDeductions = deductions.filter((d) => {
    const day = (d.created_at || "").slice(0, 10);
    if (from && day < from) return false;
    if (to && day > to) return false;
    if (empFilter !== "all" && d.employee_name !== empFilter) return false;
    if (kindFilter !== "all" && d.kind !== kindFilter) return false;
    return true;
  });

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allVisibleSelected = visibleDeductions.length > 0
    && visibleDeductions.every((d) => selected.has(d.id));

  const openDedEdit = (d) => {
    setDedEdit(d);
    setDedDays(d.days || 1);
    setDedDetails(d.details || "");
  };

  const saveDeduction = async () => {
    try {
      await api.patch(`/deductions/${dedEdit.id}`, { days: Number(dedDays), details: dedDetails });
      toast.success("تم تحديث قرار الحسم");
      setDedEdit(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذر تحديث القرار"); }
  };

  const deleteDeduction = async (d) => {
    if (!window.confirm(`حذف قرار الحسم الخاص بـ ${d.employee_name}؟`)) return;
    try {
      await api.delete(`/deductions/${d.id}`);
      toast.success("حُذف قرار الحسم");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذر حذف القرار"); }
  };

  const bulkDeleteDeductions = async (mode) => {
    const targets = mode === "selected"
      ? deductions.filter((d) => selected.has(d.id))
      : visibleDeductions;
    if (!targets.length) return;
    setDeleting(true);
    try {
      let deleted = 0;
      for (let i = 0; i < targets.length; i += 500) {
        const { data } = await api.post("/deductions/bulk-delete",
          { ids: targets.slice(i, i + 500).map((d) => d.id) });
        deleted += data.deleted;
      }
      toast.success(`تم حذف ${deleted} قرار حسم نهائياً`);
      setSelected(new Set());
      setDeleteOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحذف"); }
    finally { setDeleting(false); }
  };

  const deleteAllDeductions = async () => {
    if (!window.confirm("تنبيه: سيتم حذف جميع قرارات الحسم نهائياً. هل أنت متأكد؟")) return;
    if (!window.confirm("تأكيد أخير: لا يمكن التراجع عن هذا الإجراء. متابعة حذف كل القرارات؟")) return;
    try {
      const { data } = await api.delete("/deductions");
      toast.success(`تم حذف ${data.deleted} قرار حسم`);
      setSelected(new Set());
      setDeleteOpen(false);
      load();
    } catch { toast.error("فشل الحذف"); }
  };

  const printDeduction = (d) => {
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("المتصفح منع النافذة المنبثقة — اسمح بالنوافذ المنبثقة ثم أعد المحاولة");
      return;
    }
    w.document.write(deductionHtml(d, branding));
    w.document.close();
    waitForPrintImages(w.document).then(() => { w.focus(); w.print(); });
  };

  const printPeriodDeductions = () => {
    if (!visibleDeductions.length) {
      toast.error("لا توجد قرارات مطابقة للتصفية المحددة");
      return;
    }
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("المتصفح منع النافذة المنبثقة — اسمح بالنوافذ المنبثقة ثم أعد المحاولة");
      return;
    }
    w.document.write(combinedDeductionsHtml(visibleDeductions, branding));
    w.document.close();
    waitForPrintImages(w.document).then(() => { w.focus(); w.print(); });
  };

  return (
    <div className="space-y-4" data-testid="deductions-tab">
      <div className="bg-white rounded-2xl border p-4 sm:p-5 space-y-3" data-testid="deductions-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-base text-slate-800">قرارات الحسم
            <span className="text-xs font-normal text-slate-400"> ({deductions.length})</span>
          </h3>
          <p className="text-[11px] text-slate-400">تُنشأ تلقائياً عند الغياب بدون عذر أو تجاوز كل 420 دقيقة تأخر متراكمة</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          <DateInput value={from} onChange={(e) => setFrom(e.target.value)}
            data-testid="deductions-filter-from" className="h-9 w-36 text-xs" placeholder="من تاريخ" />
          <DateInput value={to} onChange={(e) => setTo(e.target.value)}
            data-testid="deductions-filter-to" className="h-9 w-36 text-xs" placeholder="إلى تاريخ" />
          <Select value={empFilter} onValueChange={setEmpFilter}>
            <SelectTrigger className="h-9 w-44 text-xs" data-testid="filter-emp-deduction">
              <SelectValue placeholder="كل الموظفين" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-emp-deduction-all">كل الموظفين</SelectItem>
              {empOptions.map((n) => (
                <SelectItem key={n} value={n} data-testid={`filter-emp-deduction-${n}`}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="h-9 w-40 text-xs" data-testid="filter-kind-deduction">
              <SelectValue placeholder="كل الأنواع" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-kind-deduction-all">كل أنواع الحسم</SelectItem>
              {kindOptions.map((k) => (
                <SelectItem key={k} value={k} data-testid={`filter-kind-deduction-${k}`}>
                  {KIND_LABELS[k] || k}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(from || to || empFilter !== "all" || kindFilter !== "all") && (
            <button type="button" data-testid="btn-clear-deduction-filters"
              onClick={() => { setFrom(""); setTo(""); setEmpFilter("all"); setKindFilter("all"); }}
              className="text-xs text-slate-400 hover:text-rose-600">مسح التصفية</button>
          )}
          <div className="mr-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setDeleteOpen(true)}
              disabled={visibleDeductions.length === 0}
              data-testid="btn-delete-deductions"
              className="h-9 text-xs gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50">
              <Trash2 className="w-4 h-4" /> حذف القرارات{selected.size > 0 ? ` (${selected.size})` : ""}
            </Button>
            <Button size="sm" variant="outline" onClick={printPeriodDeductions}
              disabled={!visibleDeductions.length}
              data-testid="btn-print-period-deductions" className="h-9 text-xs gap-1.5">
              <Printer className="w-3.5 h-3.5" /> طباعة قرارات الفترة ({visibleDeductions.length})
            </Button>
          </div>
        </div>

        <div className="space-y-2" data-testid="deductions-list">
          {deductions.length === 0 && (
            <p className="text-sm text-slate-400" data-testid="deductions-empty">لا توجد قرارات حسم بعد</p>
          )}
          {deductions.length > 0 && visibleDeductions.length === 0 && (
            <p className="text-sm text-slate-400" data-testid="deductions-filtered-empty">لا توجد قرارات ضمن الفترة أو التصفية المحددة</p>
          )}
          {visibleDeductions.length > 1 && (
            <label className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 cursor-pointer px-1">
              <Checkbox checked={allVisibleSelected}
                onCheckedChange={(v) => setSelected(v ? new Set(visibleDeductions.map((d) => d.id)) : new Set())}
                data-testid="check-select-all-deductions" />
              تحديد كل الظاهر ({visibleDeductions.length})
            </label>
          )}
          {visibleDeductions.map((d) => (
            <div key={d.id} className={`flex flex-wrap items-center gap-3 border rounded-xl px-4 py-3 ${selected.has(d.id) ? "bg-orange-50/40 border-orange-200" : ""}`}
              data-testid={`deduction-row-${d.id}`}>
              <Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggleSelect(d.id)}
                data-testid={`check-select-deduction-${d.id}`} aria-label={`تحديد قرار ${d.employee_name}`} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm">{d.employee_name} — <span className="text-rose-800">{d.title}</span>
                  <span className="text-[10px] font-bold text-rose-800 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5 mr-1.5"
                    data-testid={`deduction-days-${d.id}`}>حسم {d.days} {d.days === 1 ? "يوم" : "أيام"}</span>
                </p>
                <p className="text-xs text-slate-600 mt-0.5" data-testid={`deduction-reason-${d.id}`}>{d.reason}</p>
                <p className="text-[11px] text-slate-400" data-testid={`deduction-details-${d.id}`}>{d.details}</p>
                <p className="text-[11px] text-slate-400" dir="ltr">{(d.created_at || "").slice(0, 10)}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => printDeduction(d)}
                data-testid={`btn-print-deduction-${d.id}`} className="h-8 text-xs gap-1.5">
                <Printer className="w-3.5 h-3.5" /> طباعة القرار
              </Button>
              <Button size="sm" variant="outline" onClick={() => openDedEdit(d)}
                data-testid={`btn-edit-deduction-${d.id}`} className="h-8 text-xs gap-1.5">
                تعديل
              </Button>
              <button onClick={() => deleteDeduction(d)} data-testid={`btn-delete-deduction-${d.id}`}
                className="text-rose-500 hover:text-rose-700 p-1.5">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!dedEdit} onOpenChange={() => setDedEdit(null)}>
        <DialogContent className="max-w-md" dir="rtl" data-testid="deduction-edit-modal">
          <DialogHeader><DialogTitle>تعديل قرار الحسم — {dedEdit?.employee_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-500">عدد أيام الحسم</p>
              <Input type="number" min={1} value={dedDays}
                onChange={(e) => setDedDays(e.target.value)}
                data-testid="deduction-edit-days" className="h-10 text-sm" dir="ltr" />
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-500">تفاصيل المخالفة</p>
              <Textarea value={dedDetails} onChange={(e) => setDedDetails(e.target.value)}
                data-testid="deduction-edit-details" rows={3} maxLength={500} />
            </div>
            <Button onClick={saveDeduction} data-testid="btn-save-deduction"
              className="w-full bg-emerald-900 hover:bg-emerald-950 text-white">حفظ التعديل</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md" dir="rtl" data-testid="bulk-delete-deductions-modal">
          <DialogHeader><DialogTitle>حذف قرارات الحسم</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500 leading-6">
              اختر نطاق الحذف: القرارات المحددة، أو كل الظاهر على الشاشة وفق التصفية
              {from || to ? ` (من ${from || "البداية"} إلى ${to || "اليوم"})` : ""}،
              أو جميع القرارات نهائياً. الحذف نهائي ولا يمكن التراجع عنه.
            </p>
            <div className="space-y-2">
              <Button onClick={() => bulkDeleteDeductions("selected")} disabled={deleting || selected.size === 0}
                data-testid="btn-confirm-delete-ded-selected"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white gap-1.5">
                <Trash2 className="w-4 h-4" /> حذف القرارات المحددة ({selected.size})
              </Button>
              <Button onClick={() => bulkDeleteDeductions("visible")} disabled={deleting || visibleDeductions.length === 0}
                data-testid="btn-confirm-delete-ded-visible" variant="outline"
                className="w-full border-rose-300 text-rose-700 hover:bg-rose-50 gap-1.5">
                <Trash2 className="w-4 h-4" /> حذف كل القرارات الظاهرة ({visibleDeductions.length})
              </Button>
              <Button onClick={deleteAllDeductions} disabled={deleting}
                data-testid="btn-confirm-delete-ded-all"
                className="w-full bg-rose-600 hover:bg-rose-700 text-white gap-1.5">
                <Trash2 className="w-4 h-4" /> حذف جميع القرارات نهائياً
              </Button>
            </div>
            {deleting && <p className="text-xs text-slate-400 text-center">جارٍ الحذف...</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
